import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Upload, Trash2, ShoppingBag, Package, Search, ArrowUpDown, TrendingUp, TrendingDown, ChevronLeft, ChevronRight, CalendarDays, BarChart3, GitCompareArrows, Eye, Settings2, CheckSquare, Square } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';

const COLORS = ['#10b981','#f59e0b','#3b82f6','#ef4444','#8b5cf6','#ec4899','#14b8a6','#f97316','#6366f1','#84cc16','#06b6d4','#e11d48'];
const MONTHS = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'];
const JOURS = ['Lun','Mar','Mer','Jeu','Ven','Sam','Dim'];

const fmt = (n) => {
  if (n == null || isNaN(n)) return '0,00';
  return Number(n).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const fmtShort = (n) => {
  if (n == null || isNaN(n)) return '0';
  if (Math.abs(n) >= 1000) return `${(n/1000).toFixed(1)}k`;
  return n.toFixed(0);
};

const isNotSummary = (d) => {
  const name = d.designation?.toLowerCase() || '';
  return name && !name.startsWith('total');
};

function SortableHeader({ label, sortKey, currentSort, onSort }) {
  const isActive = currentSort.key === sortKey;
  return (
    <TableHead className="cursor-pointer select-none hover:bg-muted/50" onClick={() => onSort(sortKey)}>
      <div className="flex items-center gap-1">
        {label}
        <ArrowUpDown className={`w-3 h-3 ${isActive ? 'text-primary' : 'text-muted-foreground/40'}`} />
      </div>
    </TableHead>
  );
}

// Aggregate multiple batches' items
function aggregateItems(batches, type) {
  const map = {};
  batches.forEach(b => {
    (b.items || []).forEach(item => {
      if (!isNotSummary(item)) return;
      const key = item.designation;
      if (!map[key]) {
        map[key] = { ...item };
      } else {
        if (type === 'familles') {
          map[key].quantite += item.quantite;
          map[key].prix_ht += item.prix_ht;
          map[key].tva += item.tva;
          map[key].prix_ttc += item.prix_ttc;
        } else {
          map[key].quantite += item.quantite;
          map[key].ca_marge_ht += item.ca_marge_ht;
          map[key].tva += item.tva;
          map[key].ca_ttc += item.ca_ttc;
          map[key].remise += item.remise;
        }
      }
    });
  });
  return Object.values(map);
}

export function AnalyseVentesPage() {
  const { api } = useAuth();
  const [tab, setTab] = useState('familles');
  const [view, setView] = useState('mois'); // mois, jour, periode, comparaison
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [selectedDate, setSelectedDate] = useState(null);
  const [famillesBatches, setFamillesBatches] = useState([]);
  const [articlesBatches, setArticlesBatches] = useState([]);
  const [famillesDates, setFamillesDates] = useState([]);
  const [articlesDates, setArticlesDates] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importType, setImportType] = useState('familles');
  const [importYear, setImportYear] = useState(new Date().getFullYear());
  const [importMonth, setImportMonth] = useState(new Date().getMonth() + 1);
  const [importDay, setImportDay] = useState(new Date().getDate());
  const [searchText, setSearchText] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'prix_ttc', dir: 'desc' });
  // Period filter
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  // Comparison
  const [compPeriod1From, setCompPeriod1From] = useState('');
  const [compPeriod1To, setCompPeriod1To] = useState('');
  const [compPeriod2From, setCompPeriod2From] = useState('');
  const [compPeriod2To, setCompPeriod2To] = useState('');
  const [compData1, setCompData1] = useState([]);
  const [compData2, setCompData2] = useState([]);
  const [manageOpen, setManageOpen] = useState(false);
  const [allImports, setAllImports] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [deleting, setDeleting] = useState(false);
  const fileRef = useRef(null);

  // Fetch dates for calendar dots
  const fetchDates = useCallback(async () => {
    try {
      const [fd, ad] = await Promise.all([
        api.get(`/ventes-analyse/dates?type=familles&year=${currentYear}&month=${currentMonth + 1}`),
        api.get(`/ventes-analyse/dates?type=articles&year=${currentYear}&month=${currentMonth + 1}`),
      ]);
      setFamillesDates(fd.data);
      setArticlesDates(ad.data);
    } catch {}
  }, [api, currentYear, currentMonth]);

  // Fetch batches based on view
  const fetchBatches = useCallback(async () => {
    try {
      let params = '';
      if (view === 'jour' && selectedDate) {
        params = `?date=${selectedDate}`;
      } else if (view === 'periode' && dateFrom && dateTo) {
        params = `?date_from=${dateFrom}&date_to=${dateTo}`;
      } else if (view === 'mois') {
        params = `?month=${currentMonth + 1}&year=${currentYear}`;
      }
      const [fam, art] = await Promise.all([
        api.get(`/ventes-analyse/familles${params}`),
        api.get(`/ventes-analyse/articles${params}`),
      ]);
      setFamillesBatches(fam.data);
      setArticlesBatches(art.data);
    } catch { toast.error('Erreur chargement données'); }
  }, [api, view, selectedDate, dateFrom, dateTo, currentMonth, currentYear]);

  useEffect(() => { fetchDates(); }, [fetchDates]);
  useEffect(() => { fetchBatches(); }, [fetchBatches]);

  // Calendar helpers
  const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
  const firstDayOfWeek = (new Date(currentYear, currentMonth, 1).getDay() + 6) % 7;
  const prevMonth = () => { if (currentMonth === 0) { setCurrentMonth(11); setCurrentYear(y => y - 1); } else setCurrentMonth(m => m - 1); };
  const nextMonth = () => { if (currentMonth === 11) { setCurrentMonth(0); setCurrentYear(y => y + 1); } else setCurrentMonth(m => m + 1); };

  const today = new Date();
  const isToday = (day) => today.getDate() === day && today.getMonth() === currentMonth && today.getFullYear() === currentYear;

  const getDateStr = (day) => `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  const hasFamillesData = (day) => famillesDates.some(d => d.date === getDateStr(day));
  const hasArticlesData = (day) => articlesDates.some(d => d.date === getDateStr(day));
  const getDayCA = (day) => {
    const dateStr = getDateStr(day);
    const fam = famillesDates.find(d => d.date === dateStr);
    return fam?.total_ttc || 0;
  };

  // Import handler
  const handleImport = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) { toast.error('Sélectionnez un fichier'); return; }
    const saleDate = `${importYear}-${String(importMonth).padStart(2, '0')}-${String(importDay).padStart(2, '0')}`;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('sale_date', saleDate);
      const res = await api.post(`/ventes-analyse/import-${importType}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(res.data.message);
      fileRef.current.value = '';
      setImportDialogOpen(false);
      fetchDates();
      fetchBatches();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur import');
    } finally { setUploading(false); }
  };

  const handleDelete = async (batchId) => {
    try {
      await api.delete(`/ventes-analyse/${batchId}`);
      toast.success('Import supprimé');
      fetchDates();
      fetchBatches();
      if (manageOpen) fetchAllImports();
    } catch { toast.error('Erreur suppression'); }
  };

  const fetchAllImports = async () => {
    try {
      const res = await api.get('/ventes-analyse/all-imports');
      setAllImports(res.data);
      setSelectedIds(new Set());
    } catch { toast.error('Erreur chargement imports'); }
  };

  const openManageDialog = () => {
    setManageOpen(true);
    fetchAllImports();
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === allImports.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(allImports.map(i => i.id)));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) { toast.error('Sélectionnez au moins un import'); return; }
    setDeleting(true);
    try {
      const res = await api.post('/ventes-analyse/delete-bulk', { ids: [...selectedIds] });
      toast.success(res.data.message);
      fetchAllImports();
      fetchDates();
      fetchBatches();
    } catch { toast.error('Erreur suppression'); }
    finally { setDeleting(false); }
  };

  // Comparison fetch
  const fetchComparison = async () => {
    if (!compPeriod1From || !compPeriod1To || !compPeriod2From || !compPeriod2To) {
      toast.error('Remplissez les 2 périodes'); return;
    }
    const endpoint = tab === 'familles' ? 'familles' : 'articles';
    try {
      const [r1, r2] = await Promise.all([
        api.get(`/ventes-analyse/${endpoint}?date_from=${compPeriod1From}&date_to=${compPeriod1To}`),
        api.get(`/ventes-analyse/${endpoint}?date_from=${compPeriod2From}&date_to=${compPeriod2To}`),
      ]);
      setCompData1(r1.data);
      setCompData2(r2.data);
      toast.success('Comparaison chargée');
    } catch { toast.error('Erreur comparaison'); }
  };

  // Data for current view
  const currentBatches = tab === 'familles' ? famillesBatches : articlesBatches;
  const items = aggregateItems(currentBatches, tab);

  const doSort = (data) => [...data].sort((a, b) => {
    const va = a[sortConfig.key] ?? 0;
    const vb = b[sortConfig.key] ?? 0;
    if (typeof va === 'string') return sortConfig.dir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
    return sortConfig.dir === 'asc' ? va - vb : vb - va;
  });

  const toggleSort = (key) => setSortConfig(prev => ({ key, dir: prev.key === key && prev.dir === 'desc' ? 'asc' : 'desc' }));

  const filtered = doSort(items.filter(i => {
    const ttc = tab === 'familles' ? i.prix_ttc : i.ca_ttc;
    return ttc > 0 && i.designation.toLowerCase().includes(searchText.toLowerCase());
  }));

  const chartData = [...items].filter(i => (tab === 'familles' ? i.prix_ttc : i.ca_ttc) > 0).sort((a, b) =>
    (tab === 'familles' ? b.prix_ttc - a.prix_ttc : b.ca_ttc - a.ca_ttc)
  );

  const totalTTC = chartData.reduce((s, i) => s + (tab === 'familles' ? i.prix_ttc : i.ca_ttc), 0);
  const totalQty = chartData.reduce((s, i) => s + i.quantite, 0);
  const topItem = chartData[0]?.designation || '-';

  // Daily CA evolution for the month
  const dailyCA = Array.from({ length: daysInMonth }, (_, i) => {
    const day = i + 1;
    return { jour: day, ca: getDayCA(day) };
  });

  // Comparison aggregated data
  const compItems1 = aggregateItems(compData1, tab);
  const compItems2 = aggregateItems(compData2, tab);

  const viewLabel = view === 'mois' ? `${MONTHS[currentMonth]} ${currentYear}` : view === 'jour' && selectedDate ? new Date(selectedDate + 'T12:00:00').toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) : view === 'periode' && dateFrom && dateTo ? `Du ${new Date(dateFrom + 'T12:00:00').toLocaleDateString('fr-FR')} au ${new Date(dateTo + 'T12:00:00').toLocaleDateString('fr-FR')}` : '';

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" data-testid="analyse-ventes-title">Analyse des Ventes</h1>
          <p className="text-sm text-muted-foreground mt-0.5">{viewLabel}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => { setImportType('familles'); setImportDialogOpen(true); }} className="gap-2" variant="default" data-testid="import-familles-btn">
            <Upload className="w-4 h-4" /> Import Familles
          </Button>
          <Button onClick={() => { setImportType('articles'); setImportDialogOpen(true); }} className="gap-2" variant="outline" data-testid="import-articles-btn">
            <Upload className="w-4 h-4" /> Import Articles
          </Button>
          <Button onClick={openManageDialog} className="gap-2" variant="outline" data-testid="manage-imports-btn">
            <Settings2 className="w-4 h-4" /> Gérer
          </Button>
        </div>
      </div>

      {/* Import Dialog */}
      <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Importer {importType === 'familles' ? 'Ventes par Famille' : 'Ventes par Article'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="mb-2 block">Date de vente</Label>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <Label className="text-xs text-muted-foreground">Jour</Label>
                  <Select value={String(importDay)} onValueChange={v => setImportDay(Number(v))}>
                    <SelectTrigger data-testid="import-day-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: new Date(importYear, importMonth, 0).getDate() }, (_, i) => i + 1).map(d => (
                        <SelectItem key={d} value={String(d)}>{d}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Mois</Label>
                  <Select value={String(importMonth)} onValueChange={v => { setImportMonth(Number(v)); const maxD = new Date(importYear, Number(v), 0).getDate(); if (importDay > maxD) setImportDay(maxD); }}>
                    <SelectTrigger data-testid="import-month-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {MONTHS.map((m, i) => (
                        <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Année</Label>
                  <Select value={String(importYear)} onValueChange={v => setImportYear(Number(v))}>
                    <SelectTrigger data-testid="import-year-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {[2023, 2024, 2025, 2026, 2027].map(y => (
                        <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-1.5">
                Date sélectionnée : <span className="font-medium">{importDay} {MONTHS[importMonth - 1]} {importYear}</span>
              </p>
            </div>
            <div>
              <Label>Fichier CSV</Label>
              <Input ref={fileRef} type="file" accept=".csv,.xlsx" data-testid="import-file-input" />
            </div>
            <Button onClick={handleImport} disabled={uploading} className="w-full gap-2" data-testid="import-submit-btn">
              <Upload className="w-4 h-4" /> {uploading ? 'Import en cours...' : 'Importer'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Manage Imports Dialog */}
      <Dialog open={manageOpen} onOpenChange={setManageOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>Gérer les imports</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {/* Actions bar */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={toggleSelectAll} className="gap-1.5 text-xs" data-testid="select-all-imports">
                  {selectedIds.size === allImports.length && allImports.length > 0 ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
                  {selectedIds.size === allImports.length && allImports.length > 0 ? 'Tout désélectionner' : 'Tout sélectionner'}
                </Button>
                <span className="text-xs text-muted-foreground">{allImports.length} import(s)</span>
              </div>
              {selectedIds.size > 0 && (
                <Button variant="destructive" size="sm" onClick={handleBulkDelete} disabled={deleting} className="gap-1.5" data-testid="bulk-delete-btn">
                  <Trash2 className="w-4 h-4" /> {deleting ? 'Suppression...' : `Supprimer (${selectedIds.size})`}
                </Button>
              )}
            </div>
            {/* List */}
            <div className="max-h-[50vh] overflow-auto border rounded-md">
              {allImports.length === 0 ? (
                <div className="py-8 text-center text-muted-foreground text-sm">Aucun import</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10"></TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Fichier</TableHead>
                      <TableHead>Total TTC</TableHead>
                      <TableHead>Importé le</TableHead>
                      <TableHead className="w-10"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {allImports.map(imp => (
                      <TableRow key={imp.id} className={selectedIds.has(imp.id) ? 'bg-destructive/5' : ''}>
                        <TableCell>
                          <button onClick={() => toggleSelect(imp.id)} className="p-0.5" data-testid={`select-import-${imp.id}`}>
                            {selectedIds.has(imp.id) ? <CheckSquare className="w-4 h-4 text-destructive" /> : <Square className="w-4 h-4 text-muted-foreground" />}
                          </button>
                        </TableCell>
                        <TableCell className="font-medium">{new Date(imp.date + 'T12:00:00').toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}</TableCell>
                        <TableCell>
                          <Badge variant={imp.type === 'familles' ? 'default' : 'outline'} className="text-xs">
                            {imp.type === 'familles' ? 'Familles' : 'Articles'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">{imp.filename}</TableCell>
                        <TableCell className="font-medium">{Number(imp.total_ttc || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} &euro;</TableCell>
                        <TableCell className="text-xs text-muted-foreground">{new Date(imp.imported_at).toLocaleDateString('fr-FR')}</TableCell>
                        <TableCell>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive" onClick={() => handleDelete(imp.id)} data-testid={`delete-import-${imp.id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Selector + Tabs */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex gap-1 bg-muted p-1 rounded-lg">
          {[
            { id: 'mois', icon: CalendarDays, label: 'Mois' },
            { id: 'jour', icon: Eye, label: 'Jour' },
            { id: 'periode', icon: BarChart3, label: 'Période' },
            { id: 'comparaison', icon: GitCompareArrows, label: 'Comparer' },
          ].map(v => (
            <Button key={v.id} variant={view === v.id ? 'default' : 'ghost'} size="sm" className="gap-1.5 text-xs" onClick={() => setView(v.id)} data-testid={`view-${v.id}`}>
              <v.icon className="w-3.5 h-3.5" /> {v.label}
            </Button>
          ))}
        </div>
        <Tabs value={tab} onValueChange={v => { setTab(v); setSortConfig({ key: v === 'familles' ? 'prix_ttc' : 'ca_ttc', dir: 'desc' }); }}>
          <TabsList>
            <TabsTrigger value="familles" className="gap-1.5 text-xs" data-testid="tab-familles"><ShoppingBag className="w-3.5 h-3.5" /> Familles</TabsTrigger>
            <TabsTrigger value="articles" className="gap-1.5 text-xs" data-testid="tab-articles"><Package className="w-3.5 h-3.5" /> Articles</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* ===== VUE MOIS ===== */}
      {view === 'mois' && (
        <div className="space-y-5">
          {/* Month Nav + Calendar */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <Button variant="ghost" size="icon" onClick={prevMonth} data-testid="prev-month"><ChevronLeft className="w-5 h-5" /></Button>
                <CardTitle className="text-lg">{MONTHS[currentMonth]} {currentYear}</CardTitle>
                <Button variant="ghost" size="icon" onClick={nextMonth} data-testid="next-month"><ChevronRight className="w-5 h-5" /></Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-7 gap-1">
                {JOURS.map(d => <div key={d} className="text-center text-xs font-medium text-muted-foreground py-1.5">{d}</div>)}
                {Array.from({ length: firstDayOfWeek }).map((_, i) => <div key={`e-${i}`} />)}
                {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(day => {
                  const hasFam = hasFamillesData(day);
                  const hasArt = hasArticlesData(day);
                  const hasData = hasFam || hasArt;
                  const ca = getDayCA(day);
                  return (
                    <div
                      key={day}
                      onClick={() => { if (hasData) { setSelectedDate(getDateStr(day)); setView('jour'); } }}
                      className={`min-h-[60px] p-1.5 border rounded-md text-xs cursor-pointer transition-colors ${isToday(day) ? 'bg-primary/10 border-primary' : hasData ? 'hover:bg-accent border-border' : 'opacity-50 border-border/30'}`}
                      data-testid={`cal-day-${day}`}
                    >
                      <div className={`font-medium ${isToday(day) ? 'text-primary' : ''}`}>{day}</div>
                      {hasData && (
                        <div className="mt-1 space-y-0.5">
                          {ca > 0 && <div className="text-[10px] font-semibold text-emerald-600">{fmtShort(ca)} &euro;</div>}
                          <div className="flex gap-0.5">
                            {hasFam && <div className="w-2 h-2 rounded-full bg-blue-500" title="Familles" />}
                            {hasArt && <div className="w-2 h-2 rounded-full bg-orange-500" title="Articles" />}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-500" /> Familles</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-orange-500" /> Articles</span>
              </div>
            </CardContent>
          </Card>

          {/* Daily CA Chart */}
          {dailyCA.some(d => d.ca > 0) && (
            <Card>
              <CardHeader><CardTitle className="text-base">CA Journalier - {MONTHS[currentMonth]} {currentYear}</CardTitle></CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={dailyCA}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                    <XAxis dataKey="jour" tick={{ fontSize: 10 }} />
                    <YAxis tickFormatter={v => fmtShort(v)} />
                    <Tooltip formatter={(v) => `${fmt(v)} \u20ac`} labelFormatter={l => `Jour ${l}`} />
                    <Bar dataKey="ca" name="CA TTC" fill="#10b981" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          {/* Month summary */}
          {items.length > 0 && <DataView items={filtered} chartData={chartData} tab={tab} totalTTC={totalTTC} totalQty={totalQty} topItem={topItem} nbItems={chartData.length} sortConfig={sortConfig} toggleSort={toggleSort} searchText={searchText} setSearchText={setSearchText} batches={currentBatches} onDelete={handleDelete} label={`${MONTHS[currentMonth]} ${currentYear}`} nbDays={currentBatches.length} />}
        </div>
      )}

      {/* ===== VUE JOUR ===== */}
      {view === 'jour' && (
        <div className="space-y-5">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-4 flex-wrap">
                <Label className="font-medium">Sélectionner un jour :</Label>
                <Input type="date" value={selectedDate || ''} onChange={e => setSelectedDate(e.target.value)} className="w-auto" data-testid="day-picker" />
                <Button variant="outline" size="sm" onClick={() => { setView('mois'); }} data-testid="back-to-month">Retour au mois</Button>
              </div>
            </CardContent>
          </Card>
          {items.length > 0 ? (
            <DataView items={filtered} chartData={chartData} tab={tab} totalTTC={totalTTC} totalQty={totalQty} topItem={topItem} nbItems={chartData.length} sortConfig={sortConfig} toggleSort={toggleSort} searchText={searchText} setSearchText={setSearchText} batches={currentBatches} onDelete={handleDelete} label={selectedDate ? new Date(selectedDate + 'T12:00:00').toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) : ''} nbDays={1} />
          ) : (
            <Card><CardContent className="py-12 text-center text-muted-foreground">
              <CalendarDays className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="font-medium">Aucune donnée pour ce jour</p>
              <p className="text-sm mt-1">Importez un fichier CSV pour cette date</p>
            </CardContent></Card>
          )}
        </div>
      )}

      {/* ===== VUE PÉRIODE ===== */}
      {view === 'periode' && (
        <div className="space-y-5">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-4 flex-wrap">
                <div>
                  <Label className="text-xs">Du</Label>
                  <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="w-auto" data-testid="period-from" />
                </div>
                <div>
                  <Label className="text-xs">Au</Label>
                  <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="w-auto" data-testid="period-to" />
                </div>
                <div className="flex gap-2 mt-4">
                  <Button size="sm" variant="outline" onClick={() => { const now = new Date(); const y = now.getFullYear(); const m = now.getMonth(); setDateFrom(`${y}-${String(m+1).padStart(2,'0')}-01`); setDateTo(`${y}-${String(m+1).padStart(2,'0')}-${new Date(y,m+1,0).getDate()}`); }}>Ce mois</Button>
                  <Button size="sm" variant="outline" onClick={() => { const now = new Date(); const y = now.getFullYear(); setDateFrom(`${y}-01-01`); setDateTo(`${y}-12-31`); }}>Cette année</Button>
                </div>
              </div>
            </CardContent>
          </Card>
          {items.length > 0 ? (
            <DataView items={filtered} chartData={chartData} tab={tab} totalTTC={totalTTC} totalQty={totalQty} topItem={topItem} nbItems={chartData.length} sortConfig={sortConfig} toggleSort={toggleSort} searchText={searchText} setSearchText={setSearchText} batches={currentBatches} onDelete={handleDelete} label={dateFrom && dateTo ? `${new Date(dateFrom+'T12:00:00').toLocaleDateString('fr-FR')} - ${new Date(dateTo+'T12:00:00').toLocaleDateString('fr-FR')}` : 'Sélectionnez une période'} nbDays={currentBatches.length} />
          ) : (
            <Card><CardContent className="py-12 text-center text-muted-foreground">
              <BarChart3 className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="font-medium">Sélectionnez une période</p>
              <p className="text-sm mt-1">Définissez les dates de début et fin pour voir les données agrégées</p>
            </CardContent></Card>
          )}
        </div>
      )}

      {/* ===== VUE COMPARAISON ===== */}
      {view === 'comparaison' && (
        <div className="space-y-5">
          <Card>
            <CardContent className="p-4">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="font-medium text-blue-600">Période 1</Label>
                  <div className="flex gap-2">
                    <div><Label className="text-xs">Du</Label><Input type="date" value={compPeriod1From} onChange={e => setCompPeriod1From(e.target.value)} data-testid="comp-p1-from" /></div>
                    <div><Label className="text-xs">Au</Label><Input type="date" value={compPeriod1To} onChange={e => setCompPeriod1To(e.target.value)} data-testid="comp-p1-to" /></div>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="font-medium text-orange-600">Période 2</Label>
                  <div className="flex gap-2">
                    <div><Label className="text-xs">Du</Label><Input type="date" value={compPeriod2From} onChange={e => setCompPeriod2From(e.target.value)} data-testid="comp-p2-from" /></div>
                    <div><Label className="text-xs">Au</Label><Input type="date" value={compPeriod2To} onChange={e => setCompPeriod2To(e.target.value)} data-testid="comp-p2-to" /></div>
                  </div>
                </div>
              </div>
              <Button onClick={fetchComparison} className="mt-4 gap-2" data-testid="compare-btn">
                <GitCompareArrows className="w-4 h-4" /> Comparer
              </Button>
            </CardContent>
          </Card>

          {compItems1.length > 0 && compItems2.length > 0 && (
            <ComparisonView items1={compItems1} items2={compItems2} tab={tab} label1={`${compPeriod1From} → ${compPeriod1To}`} label2={`${compPeriod2From} → ${compPeriod2To}`} batches1={compData1} batches2={compData2} />
          )}
        </div>
      )}
    </div>
  );
}

// ===== DataView Component =====
function DataView({ items, chartData, tab, totalTTC, totalQty, topItem, nbItems, sortConfig, toggleSort, searchText, setSearchText, batches, onDelete, label, nbDays }) {
  const top20 = chartData.slice(0, 20);
  const pieData = (tab === 'familles' ? chartData : chartData.slice(0, 10)).map(i => ({
    name: i.designation,
    value: tab === 'familles' ? i.prix_ttc : i.ca_ttc,
  }));

  return (
    <div className="space-y-5">
      {/* KPI */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card><CardContent className="p-3 text-center">
          <p className="text-[11px] text-muted-foreground mb-0.5">CA Total TTC</p>
          <p className="text-lg font-bold text-primary" data-testid="total-ttc">{fmt(totalTTC)} &euro;</p>
        </CardContent></Card>
        <Card><CardContent className="p-3 text-center">
          <p className="text-[11px] text-muted-foreground mb-0.5">{tab === 'familles' ? 'Familles' : 'Articles'}</p>
          <p className="text-lg font-bold">{nbItems}</p>
        </CardContent></Card>
        <Card><CardContent className="p-3 text-center">
          <p className="text-[11px] text-muted-foreground mb-0.5">Quantité totale</p>
          <p className="text-lg font-bold">{totalQty.toLocaleString('fr-FR')}</p>
        </CardContent></Card>
        <Card><CardContent className="p-3 text-center">
          <p className="text-[11px] text-muted-foreground mb-0.5">Top</p>
          <p className="text-sm font-bold text-emerald-600 truncate">{topItem}</p>
        </CardContent></Card>
        <Card><CardContent className="p-3 text-center">
          <p className="text-[11px] text-muted-foreground mb-0.5">Jours importés</p>
          <p className="text-lg font-bold">{nbDays}</p>
        </CardContent></Card>
      </div>

      {/* Charts */}
      <div className="grid md:grid-cols-2 gap-5">
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">CA TTC par {tab === 'familles' ? 'Famille' : 'Article'}</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={Math.max(250, top20.length * 28)}>
              <BarChart data={top20} layout="vertical" margin={{ left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis type="number" tickFormatter={v => fmtShort(v)} />
                <YAxis type="category" dataKey="designation" width={140} tick={{ fontSize: 10 }} />
                <Tooltip formatter={(v) => `${fmt(v)} \u20ac`} />
                <Bar dataKey={tab === 'familles' ? 'prix_ttc' : 'ca_ttc'} name="CA TTC" radius={[0, 4, 4, 0]}>
                  {top20.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Répartition CA</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={110} label={({ name, percent }) => percent > 0.03 ? `${name.substring(0, 12)} ${(percent * 100).toFixed(1)}%` : ''} labelLine={false}>
                  {pieData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => `${fmt(v)} \u20ac`} />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Imports list */}
      {batches.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {batches.map(b => (
            <Badge key={b.id} variant="outline" className="gap-1.5 py-1">
              <span className="text-xs">{b.date}</span>
              <span className="text-xs text-muted-foreground">{b.filename}</span>
              <button onClick={() => onDelete(b.id)} className="ml-1 text-destructive hover:text-destructive/80"><Trash2 className="w-3 h-3" /></button>
            </Badge>
          ))}
        </div>
      )}

      {/* Table */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm">Détail {label}</CardTitle>
            <div className="relative w-56">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
              <Input value={searchText} onChange={e => setSearchText(e.target.value)} placeholder="Rechercher..." className="pl-8 h-8 text-xs" data-testid="search-input" />
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="max-h-[500px] overflow-auto">
            {tab === 'familles' ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <SortableHeader label="Famille" sortKey="designation" currentSort={sortConfig} onSort={toggleSort} />
                    <SortableHeader label="Quantité" sortKey="quantite" currentSort={sortConfig} onSort={toggleSort} />
                    <SortableHeader label="Prix HT" sortKey="prix_ht" currentSort={sortConfig} onSort={toggleSort} />
                    <SortableHeader label="TVA" sortKey="tva" currentSort={sortConfig} onSort={toggleSort} />
                    <SortableHeader label="Prix TTC" sortKey="prix_ttc" currentSort={sortConfig} onSort={toggleSort} />
                    <SortableHeader label="%" sortKey="pourcentage" currentSort={sortConfig} onSort={toggleSort} />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.length === 0 ? (
                    <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">Aucune donnée</TableCell></TableRow>
                  ) : items.map((f, i) => (
                    <TableRow key={i}>
                      <TableCell className="font-medium">{f.designation}</TableCell>
                      <TableCell>{f.quantite}</TableCell>
                      <TableCell>{fmt(f.prix_ht)} &euro;</TableCell>
                      <TableCell>{fmt(f.tva)} &euro;</TableCell>
                      <TableCell className="font-semibold">{fmt(f.prix_ttc)} &euro;</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={f.pourcentage >= 0 ? 'text-emerald-700 bg-emerald-50' : 'text-red-700 bg-red-50'}>
                          {f.pourcentage >= 0 ? <TrendingUp className="w-3 h-3 mr-1" /> : <TrendingDown className="w-3 h-3 mr-1" />}
                          {typeof f.pourcentage === 'number' ? f.pourcentage.toFixed(2) : f.pourcentage}%
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <SortableHeader label="Article" sortKey="designation" currentSort={sortConfig} onSort={toggleSort} />
                    <SortableHeader label="Qté" sortKey="quantite" currentSort={sortConfig} onSort={toggleSort} />
                    <SortableHeader label="P.Unit." sortKey="prix_unitaire" currentSort={sortConfig} onSort={toggleSort} />
                    <SortableHeader label="CA Marge HT" sortKey="ca_marge_ht" currentSort={sortConfig} onSort={toggleSort} />
                    <SortableHeader label="TVA" sortKey="tva" currentSort={sortConfig} onSort={toggleSort} />
                    <SortableHeader label="CA TTC" sortKey="ca_ttc" currentSort={sortConfig} onSort={toggleSort} />
                    <SortableHeader label="Remise" sortKey="remise" currentSort={sortConfig} onSort={toggleSort} />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.length === 0 ? (
                    <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">Aucune donnée</TableCell></TableRow>
                  ) : items.map((a, i) => (
                    <TableRow key={i}>
                      <TableCell className="font-medium max-w-[200px] truncate" title={a.designation}>{a.designation}</TableCell>
                      <TableCell>{a.quantite}</TableCell>
                      <TableCell>{fmt(a.prix_unitaire)} &euro;</TableCell>
                      <TableCell>{fmt(a.ca_marge_ht)} &euro;</TableCell>
                      <TableCell>{fmt(a.tva)} &euro;</TableCell>
                      <TableCell className="font-semibold">{fmt(a.ca_ttc)} &euro;</TableCell>
                      <TableCell>{a.remise > 0 ? <Badge variant="outline" className="text-orange-700 bg-orange-50">{fmt(a.remise)} &euro;</Badge> : '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ===== ComparisonView Component =====
function ComparisonView({ items1, items2, tab, label1, label2, batches1, batches2 }) {
  const valKey = tab === 'familles' ? 'prix_ttc' : 'ca_ttc';
  const total1 = items1.filter(i => i[valKey] > 0).reduce((s, i) => s + i[valKey], 0);
  const total2 = items2.filter(i => i[valKey] > 0).reduce((s, i) => s + i[valKey], 0);
  const diff = total1 - total2;
  const diffPct = total2 > 0 ? ((diff / total2) * 100).toFixed(1) : 0;

  // Build comparison data per item
  const allNames = new Set([...items1.map(i => i.designation), ...items2.map(i => i.designation)]);
  const compRows = [...allNames].map(name => {
    const i1 = items1.find(i => i.designation === name);
    const i2 = items2.find(i => i.designation === name);
    const v1 = i1 ? i1[valKey] : 0;
    const v2 = i2 ? i2[valKey] : 0;
    if (v1 <= 0 && v2 <= 0) return null;
    return { designation: name, p1: v1, p2: v2, diff: v1 - v2, diffPct: v2 > 0 ? ((v1 - v2) / v2 * 100) : v1 > 0 ? 100 : 0 };
  }).filter(Boolean).sort((a, b) => b.p1 - a.p1);

  // Chart data: top 10
  const chartComp = compRows.slice(0, 10);

  return (
    <div className="space-y-5">
      {/* KPI Comparison */}
      <div className="grid md:grid-cols-3 gap-4">
        <Card className="border-blue-200 bg-blue-50/30">
          <CardContent className="p-4 text-center">
            <p className="text-xs text-blue-600 font-medium mb-1">Période 1</p>
            <p className="text-xs text-muted-foreground mb-2">{label1}</p>
            <p className="text-xl font-bold text-blue-700">{fmt(total1)} &euro;</p>
            <p className="text-xs text-muted-foreground">{batches1.length} jour(s)</p>
          </CardContent>
        </Card>
        <Card className={`border-2 ${diff >= 0 ? 'border-emerald-300 bg-emerald-50/30' : 'border-red-300 bg-red-50/30'}`}>
          <CardContent className="p-4 text-center">
            <p className="text-xs text-muted-foreground mb-1">Évolution</p>
            <div className="flex items-center justify-center gap-2">
              {diff >= 0 ? <TrendingUp className="w-6 h-6 text-emerald-600" /> : <TrendingDown className="w-6 h-6 text-red-600" />}
              <p className={`text-xl font-bold ${diff >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>{diff >= 0 ? '+' : ''}{fmt(diff)} &euro;</p>
            </div>
            <p className={`text-sm font-medium ${diff >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{diff >= 0 ? '+' : ''}{diffPct}%</p>
          </CardContent>
        </Card>
        <Card className="border-orange-200 bg-orange-50/30">
          <CardContent className="p-4 text-center">
            <p className="text-xs text-orange-600 font-medium mb-1">Période 2</p>
            <p className="text-xs text-muted-foreground mb-2">{label2}</p>
            <p className="text-xl font-bold text-orange-700">{fmt(total2)} &euro;</p>
            <p className="text-xs text-muted-foreground">{batches2.length} jour(s)</p>
          </CardContent>
        </Card>
      </div>

      {/* Comparison Chart */}
      <Card>
        <CardHeader><CardTitle className="text-sm">Comparaison Top 10</CardTitle></CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={350}>
            <BarChart data={chartComp} layout="vertical" margin={{ left: 10 }}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
              <XAxis type="number" tickFormatter={v => fmtShort(v)} />
              <YAxis type="category" dataKey="designation" width={140} tick={{ fontSize: 10 }} />
              <Tooltip formatter={(v) => `${fmt(v)} \u20ac`} />
              <Bar dataKey="p1" name="Période 1" fill="#3b82f6" radius={[0, 4, 4, 0]} />
              <Bar dataKey="p2" name="Période 2" fill="#f97316" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Comparison Table */}
      <Card>
        <CardHeader><CardTitle className="text-sm">Détail Comparatif</CardTitle></CardHeader>
        <CardContent className="p-0">
          <div className="max-h-[400px] overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{tab === 'familles' ? 'Famille' : 'Article'}</TableHead>
                  <TableHead className="text-blue-600">Période 1</TableHead>
                  <TableHead className="text-orange-600">Période 2</TableHead>
                  <TableHead>Différence</TableHead>
                  <TableHead>Évolution</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {compRows.map((r, i) => (
                  <TableRow key={i}>
                    <TableCell className="font-medium max-w-[200px] truncate">{r.designation}</TableCell>
                    <TableCell>{fmt(r.p1)} &euro;</TableCell>
                    <TableCell>{fmt(r.p2)} &euro;</TableCell>
                    <TableCell className={r.diff >= 0 ? 'text-emerald-600' : 'text-red-600'}>{r.diff >= 0 ? '+' : ''}{fmt(r.diff)} &euro;</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={r.diffPct >= 0 ? 'text-emerald-700 bg-emerald-50' : 'text-red-700 bg-red-50'}>
                        {r.diffPct >= 0 ? <TrendingUp className="w-3 h-3 mr-1" /> : <TrendingDown className="w-3 h-3 mr-1" />}
                        {r.diffPct >= 0 ? '+' : ''}{r.diffPct.toFixed(1)}%
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
